import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { t } from "i18next";
//components
import ItemHeader from "components/item/ItemHeader";
import ItemFooter from "components/item/ItemFooter";
import WithLoading from "components/Hoc/WithLoading";
import ModiferGroups from "components/Modifier/ModiferGroups";
import Quantity from "components/item/Quantity";
//actions
import { UTILS } from "utils";
import { NotPersistAction, ItemAction, CartAction } from "store/actions";
import { getDiscount, getDiscountedPrice, getBasketValue as getDisplayValue } from "utils/discounts.utils";
//hooks
import { useLang, useCustomHistory, useWindowLocation } from "hooks";
//constants
import C from "Constants";
import { useQueryParam } from "modules/dine-in/hooks";
import ItemNotFoundError from "components/item/ItemNotFound";
import useGetItemById from "components/item/hooks/useGetItemById";
import useAddItemToBasket from "components/item/hooks/useAddItemToBasket";
import MenuDisabledModal from "features/item/components/MenuDisabledModal";
import { isItemOutOfStock } from "utils/Utils";
import UpdateSetting from "../../../../app/util";
import { ResetAction } from "store/actions";

//TODO: remove the page interaction from the item container, use only item detail modal for page interaction,
//also remove it from the routes and browser history file

const ItemContainer = (props) => {
  // Hooks
  const dispatch = useDispatch();
  const rawParams = useParams();
  const { fetchDineStoreMenu } = UpdateSetting();
  const { currentLang } = useLang();
  const queryParam = useQueryParam();
  const router = useCustomHistory();
  const { getItemById } = useGetItemById();
  const { addItemToBasket } = useAddItemToBasket();
  const windowRouter = useWindowLocation();

  // use props params if available else use rawParams, 
  // this is used to use the item component both as a page and as a modal
  const params =  props?.params ?? rawParams

  // Redux selectors
  const { isLoading } = useSelector((state) => state.loader);
  const { selectedStore, storeMenu } = useSelector((state) => state?.store);
  const { DomainType } = useSelector((state) => state.AppReducer);
  const { discounts } = useSelector((state) => state?.brand);
  const { brandInfo } = useSelector((state) => state?.brandStorageReducer);
  const { selectedItem, error } = useSelector((state) => state?.item);
  const { orderType } = useSelector((state) => state.AppReducer);
  // React state
  const [discountPrice, setDiscountPrice] = useState(null);
  const [itemSection, setItemSection] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [singleItem, setSingleItem] = useState({
    quantity: 1,
    itemSection: [],
    price: selectedItem?.itemInfo?.price || 0,
  });

  useEffect(() => {
    updateLocaleState("price", selectedItem?.itemInfo?.price || 0);
    return () => {
      dispatch(NotPersistAction.setRequiredMissingSections([]));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem?.itemInfo?.price]);

  const updateLocaleState = (key, val) => {
    setSingleItem((prevSingleItem) => ({
      ...prevSingleItem,
      [key]: val,
    }));
  };

  const decreaseQuantity = () => {
    if (singleItem?.quantity > 1) {
      updateLocaleState("quantity", singleItem.quantity - 1);
    }
  };
  const increaseQuantity = () => {
    if (selectedItem?.itemInfo?.isStockEnabled && (singleItem?.quantity + 1) > selectedItem?.itemInfo?.availableStock) {
      dispatch(CartAction.setCartError('Maximum available quantity reached'));
      return;
    }
    updateLocaleState("quantity", singleItem.quantity + 1);
  };

  const handleClose = ()=>{
    if(props.handleClose){
      props.handleClose()
    } else closeItemDetail()
  }

  const addSelectedToBasket = () => {
    addItemToBasket(
      brandInfo,
      selectedItem,
      singleItem,
      itemSection,
      handleClose,
      setErrorMessage,
      discountPrice
    );
  };

  useEffect(() => {
    if (params?.itemId && brandInfo?.brand?._id) {
      getItemById(selectedStore, storeMenu, params, brandInfo, props?.type==="modal");
    }
    updateUtmMedium();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    params?.itemId,
    selectedStore?._id,
    brandInfo?.brand?._id,
    params?.catId,
    storeMenu?._id,
  ]);

  // update Utm_Medium
  const updateUtmMedium = () => {
    const app_loaded_through =
      !selectedStore && params?.itemId
        ? C.APP_LOADED_THROUGH.DirectLink
        : C.APP_LOADED_THROUGH.normalFlow;
    dispatch(ItemAction.updateAppLoadedThrough(app_loaded_through));
  };

  const applicableDiscount = useMemo(() => {
    return getDiscount({discounts, item: selectedItem?.itemInfo, storeId: selectedStore?._id, orderType: orderType?.serviceType});
  }, [discounts, selectedItem?.itemInfo]);


  const getBasketValue = () => {
    return getDisplayValue({price: singleItem?.price, quantity: singleItem?.quantity, discountPrice: discountPrice});
  };

  const RenderFooter = () => {
    const commonProps = {
      onButtonClick: closeItemDetail,
      btnText1: t("continue shopping"),
      ...{ selectedItem }, // Include other common props here
    };

    if (
      !selectedStore?._id ||
      !selectedStore?.isOpened ||
      isItemOutOfStock(selectedItem?.itemInfo, selectedStore)
    ) {
      return <ItemFooter {...commonProps} />;
    } else
      return (
        <ItemFooter
          onButtonClick={addSelectedToBasket}
          btnText1={`${t("Add")} ${singleItem?.quantity ?? 1} ${t(
            "to basket"
          )}`}
          btnText2={`${
            currentLang === C.AR
              ? getBasketValue() + " " + t("AED")
              : t("AED") + " " + getBasketValue()
          }`}
          {...{ selectedItem }}
        />
      );
  };

  const closeItemDetail = () => {
    router(
      "/dine-in",
      `/${params.domainName}/dine-in`,
      DomainType,
      { fromItemScreen: true },
      `?${queryParam}`
    );
  };

  const updateMenuActiveStatus = () => {
    dispatch(ItemAction.setMenuActiveStatus(false));
    dispatch(ResetAction.removeOrderData());
    const brandData = {
      id: brandInfo?.brand._id,
    };
    if (brandInfo?.brand?._id) {
      fetchDineStoreMenu(brandData);
    } else {
      windowRouter(
        `/dine-in/?${queryParam}`,
        `/${params.domainName}/dine-in/?${queryParam}`,
        DomainType
      );
    }
    closeItemDetail();
  };

  useEffect(() => {
    if(singleItem?.price && applicableDiscount){
      const discount = getDiscountedPrice(singleItem?.price);
      if(discount?.discountedPrice || discount?.discountInfoMessage){
        setDiscountPrice(discount);
      }
    }
  }, [singleItem?.price, applicableDiscount, singleItem?.quantity]);

  if (!isLoading && error)
    return <ItemNotFoundError {...{ error }} handleClick={closeItemDetail} />;
  
  return (
    <WithLoading>
      <div className="flex w-full bg-white flex-col store-page-popup">
        <ItemHeader
          {...{ selectedItem, closeItemDetail }}
          menuType="DINE_IN"
          stripText={UTILS.getStoreAndItemStatusText(
            selectedItem?.itemInfo,
            selectedStore
          )}
        />
        <div className="flex flex-grow overflow-y-auto overflow-x-hidden">
          <div className="w-full">
            <ModiferGroups
              {...{
                selectedItem,
                setItemSection,
                itemSection,
                singleItem,
                setSingleItem,
              }}
              isStoreSelected={true}
            />
            <Quantity {...{ decreaseQuantity, increaseQuantity, singleItem }} />
            <RenderFooter />
          </div>
        </div>
      </div>
      <MenuDisabledModal handleRefresh={updateMenuActiveStatus} />
    </WithLoading>
  );
};

export default ItemContainer;
